import * as React from 'react';
import { uniq } from 'lodash';
import { Error404, PermissionPage, SoftwarePermissionWrappedProvider } from 'ui-lib';
import {
    useAuthStore,
    useRoutesStore,
    useStores
} from '../../../store';
import { Navigate, Route, Routes } from 'react-router-dom';
import { DeploymentsManagementTable } from './deployments/list';
import { DeploymentCreateWizard } from './deployments/create/deployment-creation-wizard';
import { ManageSoftwareSnapshotsComponent } from './software-snapshots';
import { ManageDeploymentGroupPage } from './deployment-group';
import { RepositoriesAndPackages } from './repositories-and-packages';
import { ArtifactListingPage } from './raptor/artifacts';
import { UploadArtifactPage } from './raptor/artifacts/upload-artifact';
import { RaptorComponentsPage } from './raptor/components/list';
import { CreateComponentForm } from './raptor/components/save';
import { CreateComponentVersionForm } from './raptor/components/version/version-create';
import { EditComponentVersionForm } from './raptor/components/version/version-edit';

export enum SOFTWARE_MANAGEMENT_ROUTES {
    ROOT = 'SOFTWARE_MANAGEMENT',
    DEPLOYMENTS_VIEW = 'SOFTWARE_MANAGEMENT_DEPLOYMENTS_VIEW',
    DEPLOYMENTS_MODIFY = 'SOFTWARE_MANAGEMENT_DEPLOYMENTS_MODIFY',
    REPOSITORIES_AND_PACKAGES = 'SOFTWARE_MANAGEMENT_REPOSITORIES_AND_PACKAGES',
    SNAPSHOTS_VIEW = 'SOFTWARE_MANAGEMENT_SNAPSHOTS_VIEW',
    SNAPSHOTS_MODIFY = 'SOFTWARE_MANAGEMENT_SNAPSHOTS_MODIFY',
    DEPLOYMENTS_GROUPS_VIEW = 'SOFTWARE_MANAGEMENT_DEPLOYMENTS_GROUPS_VIEW',
    RAPTOR_COMPONENTS = 'RAPTOR_COMPONENTS_VIEW',
    RAPTOR_ARTIFACTS = 'RAPTOR_ARTIFACTS_VIEW',
    RAPTOR_ARTIFACTS_UPLOAD = 'RAPTOR_ARTIFACTS_UPLOAD',
    RAPTOR_COMPONENTS_CREATE = 'RAPTOR_COMPONENTS_CREATE',
    RAPTOR_COMPONENT_VERSIONS_CREATE = 'RAPTOR_COMPONENT_VERSIONS_CREATE',
    RAPTOR_COMPONENT_VERSIONS_EDIT = 'RAPTOR_COMPONENT_VERSIONS_EDIT',
}

const NO_TIMEZONE = 'No timezone';

export const getTimezones = (deviceGroupNames: string[], callback: (deviceGroupName: string) => string): string =>
    uniq(deviceGroupNames?.map((name: string) => callback(name) || NO_TIMEZONE)).join(', ');

export function SoftwareManagementPage() {
    const routeStore = useRoutesStore();
    const { permissionStore, packagesStore, packageGroupsStore, deploymentGroupsStore, snapshotStore } = useStores();

    const authStore = useAuthStore();

    const onLoad = () => {
        (async () => {
            const currentAccountId = authStore?.assureApiAccount?.accountId;
            await Promise.all([
                deploymentGroupsStore.loadDeploymentGroups(currentAccountId),
                packagesStore.loadEntities(currentAccountId),
                packageGroupsStore.loadEntities(currentAccountId),
                snapshotStore.loadEntities(currentAccountId),
            ]);
        })();
    };

    React.useEffect(onLoad, []);

    const rootRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.ROOT);
    const deploymentListRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.DEPLOYMENTS_VIEW);
    const deploymentCreateRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.DEPLOYMENTS_MODIFY);
    const repositoriesPackagesRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.REPOSITORIES_AND_PACKAGES);
    const snapshotRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.SNAPSHOTS_VIEW);
    const deploymentGroupsRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.DEPLOYMENTS_GROUPS_VIEW);

    const raptorComponentsRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_COMPONENTS);
    const raptorComponentCreateRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_COMPONENTS_CREATE);
    const raptorComponentVersionCreateRoute = routeStore.getRoute(
        SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_COMPONENT_VERSIONS_CREATE,
    );
    const raptorComponentVersionEditRoute = routeStore.getRoute(
        SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_COMPONENT_VERSIONS_EDIT,
    );
    const artifactsRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_ARTIFACTS);
    const artifactUploadRoute = routeStore.getRoute(SOFTWARE_MANAGEMENT_ROUTES.RAPTOR_ARTIFACTS_UPLOAD);
    return (
        <Routes>
            <Route
                path={deploymentListRoute.trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={deploymentListRoute.label}
                    >
                        <DeploymentsManagementTable />
                    </PermissionPage>
                }
            />
            <Route
                path={deploymentCreateRoute.trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={deploymentCreateRoute.label}
                    >
                        <DeploymentCreateWizard />
                    </PermissionPage>
                }
            />
            <Route
                path={deploymentListRoute.trimRootPath(rootRoute.path).replace(/\/list$/, '')}
                element={<Navigate replace to={deploymentListRoute.path} />}
            />
            <Route
                path={repositoriesPackagesRoute.trimRootPath(rootRoute.path)}
                element={
                    <SoftwarePermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={repositoriesPackagesRoute.label}
                    >
                        <RepositoriesAndPackages />
                    </SoftwarePermissionWrappedProvider>
                }
            />
            <Route
                path={snapshotRoute.trimRootPath(rootRoute.path)}
                element={
                    <PermissionPage
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={snapshotRoute.label}
                    >
                        <ManageSoftwareSnapshotsComponent />
                    </PermissionPage>
                }
            />
            <Route
                path={`${deploymentGroupsRoute.trimRootPath(rootRoute.path)}/*`}
                element={
                    <PermissionPage
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={deploymentGroupsRoute.label}
                    >
                        <ManageDeploymentGroupPage />
                    </PermissionPage>
                }
            />
            <Route
                path={`${raptorComponentCreateRoute.trimRootPath(rootRoute.path)}`}
                element={
                    <SoftwarePermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={raptorComponentCreateRoute.label}
                    >
                        <CreateComponentForm />
                    </SoftwarePermissionWrappedProvider>
                }
            />
            <Route
                path={`${raptorComponentVersionCreateRoute.trimRootPath(rootRoute.path)}`}
                element={
                    <SoftwarePermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={raptorComponentVersionCreateRoute.label}
                    >
                        <CreateComponentVersionForm />
                    </SoftwarePermissionWrappedProvider>
                }
            />
            <Route
                path={`${raptorComponentVersionEditRoute.trimRootPath(rootRoute.path)}`}
                element={
                    <SoftwarePermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={raptorComponentVersionEditRoute.label}
                    >
                        <EditComponentVersionForm />
                    </SoftwarePermissionWrappedProvider>
                }
            />
            <Route
                path={raptorComponentsRoute.trimRootPath(rootRoute.path)}
                element={
                    <SoftwarePermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={raptorComponentsRoute.label}
                    >
                        <RaptorComponentsPage />
                    </SoftwarePermissionWrappedProvider>
                }
            />
            <Route
                path={artifactsRoute.trimRootPath(rootRoute.path)}
                element={
                    <SoftwarePermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={artifactsRoute.label}
                    >
                        <ArtifactListingPage />
                    </SoftwarePermissionWrappedProvider>
                }
            />
            <Route
                path={artifactUploadRoute.trimRootPath(rootRoute.path)}
                element={
                    <SoftwarePermissionWrappedProvider
                        permissionCodes={permissionStore.permissionCodes}
                        routes={routeStore.routes}
                        title={artifactUploadRoute.label}
                    >
                        <UploadArtifactPage />
                    </SoftwarePermissionWrappedProvider>
                }
            />
            <Route path='*' element={<Error404 message='Page Not Found' />} />
        </Routes>
    );
}
